import type { ReactNode } from 'react';
import { Link, Progress } from '@backstage/core-components';
import { Typography, makeStyles } from '@material-ui/core';
import { updatedLabel } from '../../home/updatedLabel';
import {
  ACCESS_PAGE,
  sourceNotice,
  unreportedNotice,
  type ComponentSecurity,
  type ComponentSecurityAnswer,
  type Layer,
  type SourceState,
} from '../../security/security';
import {
  FindingsTable,
  LayerCellView,
  NoneOpen,
  Notices,
  NotMeasured,
} from '../../security/SecurityParts';
import { useComponentPage } from './ComponentPageContext';

const useStyles = makeStyles(theme => ({
  stack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
    minWidth: 0,
  },
  figures: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    [theme.breakpoints.down('xs')]: { gridTemplateColumns: '1fr' },
    border: '1px solid var(--ui-border)',
    borderRadius: 8,
    background: 'var(--ui-bg-elevated)',
    overflow: 'hidden',
  },
  figure: {
    padding: theme.spacing(1.5, 2),
    minWidth: 0,
    borderRight: '1px solid var(--ui-border)',
    '&:last-child': { borderRight: 'none' },
  },
  label: { fontSize: '0.8rem', color: 'var(--ui-text-muted)' },
  value: { fontSize: '1rem', fontWeight: 600, lineHeight: 2 },
  section: {
    border: '1px solid var(--ui-border)',
    borderRadius: 8,
    background: 'var(--ui-bg-elevated)',
    minWidth: 0,
    overflow: 'hidden',
  },
  sectionHead: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: theme.spacing(0.5, 2),
    padding: theme.spacing(1.25, 2),
    borderBottom: '1px solid var(--ui-border)',
  },
  heading: { margin: 0, fontSize: '1rem', fontWeight: 600 },
  source: { fontSize: '0.8rem', color: 'var(--ui-text-muted)' },
  sectionBody: { padding: theme.spacing(1.25, 2) },
  sub: { fontSize: '0.8rem', color: 'var(--ui-text-muted)' },
  message: { color: 'var(--ui-text-muted)', padding: theme.spacing(2, 0) },
}));

const Section = ({
  title,
  source,
  action,
  children,
}: {
  title: string;
  source?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) => {
  const classes = useStyles();
  return (
    <section className={classes.section} aria-label={title}>
      <div className={classes.sectionHead}>
        <div>
          <h2 className={classes.heading}>{title}</h2>
          {source && <div className={classes.source}>{source}</div>}
        </div>
        {action}
      </div>
      <div className={classes.sectionBody}>{children}</div>
    </section>
  );
};

/** One layer's sections: one per source that covers the component. */
const LayerSections = ({
  layer,
  component,
  sources,
  now,
}: {
  layer: Layer;
  component: ComponentSecurity;
  sources: SourceState[];
  now: number;
}) => {
  const classes = useStyles();
  const ofLayer = sources.filter(s => s.layer === layer.id);
  if (ofLayer.length === 0) {
    return (
      <Section title={layer.title} source="no source yet">
        <Typography className={classes.sub}>
          {unreportedNotice(layer)}
        </Typography>
      </Section>
    );
  }
  const covering = ofLayer.flatMap(source => {
    const reading = component.readings.find(r => r.source === source.id);
    return reading ? [{ source, reading }] : [];
  });
  if (covering.length === 0) {
    return (
      <Section title={layer.title}>
        <Typography className={classes.sub}>
          No source covers this component in this layer.
        </Typography>
      </Section>
    );
  }
  return (
    <>
      {covering.map(({ source, reading }) => {
        const findings = component.findings.filter(f => f.source === source.id);
        const notMeasured = component.notMeasured.filter(
          n => n.source === source.id,
        );
        let body: ReactNode;
        if (reading.state === 'unmeasured') {
          body = (
            <Typography>
              <NotMeasured reasons={[]} /> — {reading.reason}
            </Typography>
          );
        } else if (findings.length === 0 && notMeasured.length === 0) {
          body = <NoneOpen />;
        } else {
          body = (
            <FindingsTable
              source={source}
              findings={findings}
              notMeasured={notMeasured}
              now={now}
              label={`${layer.title} · ${source.label}`}
            />
          );
        }
        return (
          <Section
            key={source.id}
            title={layer.title}
            source={`${source.label} · ${component.repository}`}
            action={
              reading.state === 'read' && reading.link ? (
                <Link to={reading.link}>All of them at the source</Link>
              ) : undefined
            }
          >
            {body}
          </Section>
        );
      })}
    </>
  );
};

/** When each source last read the component. */
const ReadAt = ({ answer }: { answer: ComponentSecurityAnswer }) => {
  const classes = useStyles();
  const read = answer.sources.filter(s => s.readAt);
  return (
    <Typography className={classes.sub}>
      {read.map((source, i) => (
        <span key={source.id}>
          {i > 0 && '; '}
          {source.label} read{' '}
          <time dateTime={source.readAt!} title={source.readAt!}>
            {updatedLabel(source.readAt!)}
          </time>
        </span>
      ))}
      {read.length > 0 && '. '}
      Every component's findings: <Link to="/security">Security</Link>.
    </Typography>
  );
};

/**
 * A component's Security tab (DX-587, specs/security-findings.md AC-9): its
 * row on the Security page, a section per layer and source — the same answer
 * its tab count and its facts bar read, so the three cannot disagree.
 */
export const SecurityTab = () => {
  const classes = useStyles();
  const { security } = useComponentPage();
  const now = Date.now();
  if (!security || security.loading) return <Progress />;
  if (security.refused) {
    return (
      <Typography className={classes.message}>
        Security findings are shown to members of a team, and your sign-in names
        none the catalog holds. <Link to={ACCESS_PAGE}>How to join a team</Link>
      </Typography>
    );
  }
  if (security.error || !security.data) {
    return (
      <Typography role="alert">
        Couldn't load the security findings from github-insights.
      </Typography>
    );
  }
  const answer = security.data;
  const component = answer.component;
  if (!answer.generatedAt) {
    return (
      <Typography className={classes.message}>
        The findings have not been read yet since the portal started. They are
        read every hour; check back in a moment.
      </Typography>
    );
  }
  if (!component) {
    // Read, but not this component: it named its repository after the last
    // read, which the next one picks up.
    return (
      <Typography className={classes.message}>
        This component was not among those last read{' '}
        <time dateTime={answer.generatedAt} title={answer.generatedAt}>
          {updatedLabel(answer.generatedAt)}
        </time>
        . The findings are read every hour; it is read with the next.
      </Typography>
    );
  }
  const notices = answer.sources
    // A source that missed other components is the Security page's to name;
    // whether it missed this one, its own section says.
    .filter(source => source.state !== 'partial')
    .map(source => sourceNotice(source))
    .filter((n): n is string => Boolean(n));
  return (
    <div className={classes.stack}>
      <section aria-label="Figures" className={classes.figures}>
        {answer.layers.map(layer => (
          <div
            key={layer.id}
            className={classes.figure}
            role="group"
            aria-label={layer.title}
          >
            <div className={classes.label}>{layer.title}</div>
            <div className={classes.value}>
              <LayerCellView cell={component.layers[layer.id]} />
            </div>
          </div>
        ))}
      </section>
      <Notices notices={notices} />
      {answer.layers.map(layer => (
        <LayerSections
          key={layer.id}
          layer={layer}
          component={component}
          sources={answer.sources}
          now={now}
        />
      ))}
      <ReadAt answer={answer} />
    </div>
  );
};
